/**
 * DEMO ENGINE of the AI Assistant workspace — there is no backend.
 *
 * Maps a request (a typed prompt, a drill-down chip, a clicked table link) to a
 * scripted `AnswerMessage` built from `sample.ts`. It is deliberately a set of
 * PURE functions: no React, no timers, no token wallet. The session hook
 * (`useChatSession`) owns timing, the fake "thinking" delay and the token
 * charge; a future real engine would replace `answerFor` with an API call and
 * keep everything else.
 *
 * The engine never shows more than the caller's REAL scope: an answer whose
 * `requires` modules are not all granted (and, for typed prompts, selected in
 * the composer) is replaced by a short, non-error notice, and links / chips
 * that would lead to an unavailable answer are removed from the content.
 */
import {
  CHIP_ANSWERS,
  ENTITY_ANSWERS,
  SAMPLE_ANSWERS,
  SAMPLE_CONVERSATIONS,
  SCOPE_LABELS,
  SUGGESTED_PROMPTS,
  type SampleAnswer,
  type SampleConversation,
  type SamplePath,
} from './sample.ts';
import type {
  AnswerContent,
  AnswerContext,
  AnswerMessage,
  ChatMessage,
  ClarifyOption,
  DrillAction,
  DrillChip,
  EngineRequest,
  HeavyReason,
  PartialAccess,
  PathStep,
  ScopeKey,
  TableCellValue,
  TextPart,
} from './types.ts';

export type { EngineRequest } from './types.ts';

// ---------------------------------------------------------------------------
// Usage estimate shown under the composer
// ---------------------------------------------------------------------------

/** Typical answer (the design's empty-composer figure). */
export const ESTIMATE_BASE_TOKENS = 1200;
export const ESTIMATE_TOKENS_PER_CHAR = 6;
export const ESTIMATE_TOKENS_PER_EXTRA_MODULE = 200;
/** The base figure covers up to two modules (the design's 1,200 with فروش + انبار). */
const INCLUDED_MODULES = 2;
/** The «heavy analysis» threshold of frame G — the estimate never exceeds it. */
export const ESTIMATE_MAX_TOKENS = 4500;

/**
 * «برآورد مصرف»: an integer estimate from the prompt length and how many
 * modules the question spans. A demo formula only — the real figure would come
 * from the backend; the answer footer shows the actual (sample) usage.
 */
export function estimateTokens(prompt: string, moduleCount: number): number {
  const length = Array.from(prompt.trim()).length;
  const extraModules = Math.max(0, moduleCount - INCLUDED_MODULES);
  const raw =
    ESTIMATE_BASE_TOKENS +
    length * ESTIMATE_TOKENS_PER_CHAR +
    extraModules * ESTIMATE_TOKENS_PER_EXTRA_MODULE;
  return Math.min(ESTIMATE_MAX_TOKENS, Math.round(raw / 10) * 10);
}

// ---------------------------------------------------------------------------
// Prompt matching
// ---------------------------------------------------------------------------

/** Lower-cases, unifies Arabic/Persian letter variants, drops diacritics, ZWNJ → space, digits → ASCII. */
export function normalizePrompt(text: string): string {
  return text
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/[ً-ٰٟ]/g, '')
    .replace(/‌/g, ' ')
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[؟?!.،,؛:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

const EXACT_PROMPTS: ReadonlyMap<string, string> = new Map([
  ...SUGGESTED_PROMPTS.map((prompt): [string, string] => [
    normalizePrompt(prompt.text),
    prompt.answerId,
  ]),
  ...SAMPLE_CONVERSATIONS.map((conversation): [string, string] => [
    normalizePrompt(conversation.prompt),
    conversation.answerId,
  ]),
]);

/** The scripted answer id for a typed prompt, or `null` when nothing matches (generic answer). */
export function matchPrompt(text: string): string | null {
  const normalized = normalizePrompt(text);
  if (normalized === '') return null;
  const exact = EXACT_PROMPTS.get(normalized);
  if (exact) return exact;

  const has = (stem: string) => normalized.includes(stem);
  if (has('سود') && has('مشتری')) return 'customer-profit';
  if (has('مطالبات') || has('معوق') || has('بدهکار')) return 'receivables-60';
  if (has('نقد') || has('بانک') || has('صندوق')) return 'cash-today';
  if (has('نقطه سفارش') || (has('کالا') && has('زیر')) || has('کمبود') || has('موجودی انبار')) {
    return 'inventory-reorder';
  }
  if (has('تسک') || has('عقب افتاده') || has('وظیف')) return 'my-tasks';
  if ((has('گزارش') && has('روزانه')) || has('خلاصه مدیریتی')) return 'daily-report';
  if (has('مشتری') && has('فروش')) return 'sales-by-customer';
  if (has('فروش')) return 'sales-sep';
  return null;
}

/** Answer id a drill action leads to, or `null` for an entity with no authored sample. */
export function answerIdForAction(action: DrillAction): string | null {
  switch (action.kind) {
    case 'chip':
      return CHIP_ANSWERS[action.chipId] ?? null;
    case 'entity':
      return ENTITY_ANSWERS[action.entityId] ?? null;
    case 'prompt':
      return matchPrompt(action.text);
  }
}

// ---------------------------------------------------------------------------
// Scope gating
// ---------------------------------------------------------------------------

function isAvailable(answer: SampleAnswer | undefined, granted: readonly ScopeKey[]): boolean {
  return answer !== undefined && answer.requires.every((key) => granted.includes(key));
}

/** True when a bundled sample conversation only needs modules the caller may read. */
export function isConversationAvailable(
  conversation: SampleConversation,
  granted: readonly ScopeKey[],
): boolean {
  return isAvailable(SAMPLE_ANSWERS[conversation.answerId], granted);
}

/** Drill target usable with this scope? Entities with no authored sample stay clickable (they explain themselves). */
function isActionAvailable(action: DrillAction, granted: readonly ScopeKey[]): boolean {
  const answerId = answerIdForAction(action);
  if (answerId === null) return action.kind === 'entity';
  return isAvailable(SAMPLE_ANSWERS[answerId], granted);
}

function adaptChips(
  chips: readonly DrillChip[] | undefined,
  granted: readonly ScopeKey[],
): readonly DrillChip[] | undefined {
  if (!chips) return undefined;
  const kept = chips.filter((chip) => isActionAvailable(chip.action, granted));
  return kept.length > 0 ? kept : undefined;
}

/** Removes affordances that would lead to data outside the caller's scope. */
export function adaptContent(content: AnswerContent, granted: readonly ScopeKey[]): AnswerContent {
  const { anomaly, table, drill, ...rest } = content;
  const adaptedDrill = adaptChips(drill, granted);
  const next: {
    -readonly [K in keyof AnswerContent]: AnswerContent[K];
  } = { ...rest };
  if (adaptedDrill) next.drill = adaptedDrill;
  if (anomaly && isActionAvailable(anomaly.action.action, granted)) next.anomaly = anomaly;
  if (table) {
    next.table = {
      ...table,
      rows: table.rows.map((row) => ({
        ...row,
        cells: Object.fromEntries(
          Object.entries(row.cells).map(([key, cell]): [string, TableCellValue] => [
            key,
            typeof cell === 'object' &&
            cell.kind === 'link' &&
            !isActionAvailable(cell.action, granted)
              ? cell.text
              : cell,
          ]),
        ),
      })),
    };
  }
  return next;
}

// ---------------------------------------------------------------------------
// Answer assembly
// ---------------------------------------------------------------------------

export interface EngineContext {
  /** Modules the caller may really read (from `useAiPreview().scope`). */
  readonly granted: readonly ScopeKey[];
  /** Context of the answer the request came from (drill-downs extend its path). */
  readonly prev?: AnswerContext | undefined;
}

function buildPath(spec: SamplePath, messageId: string, prev: AnswerContext | undefined) {
  const step = (label: string, kind: PathStep['kind'], index: number): PathStep => ({
    id: `${messageId}:${index}`,
    label,
    kind,
    messageId,
  });
  const prevSteps = prev?.path ?? [];
  const withoutRecord = prevSteps.filter((s) => s.kind !== 'record');

  if (spec.mode === 'root' || prevSteps.length === 0) {
    return [step(spec.label, 'topic', 0), ...(spec.record ? [step(spec.record, 'record', 1)] : [])];
  }
  const base = spec.mode === 'entity' ? withoutRecord.slice(0, 1) : withoutRecord;
  const next = [...base, step(spec.label, spec.mode, base.length)];
  if (spec.record) next.push(step(spec.record, 'record', base.length + 1));
  return next;
}

/** A plain notice answer: no tokens, no drill-down, context kept as it was. */
function noticeAnswer(
  sampleId: string,
  messageId: string,
  text: readonly TextPart[],
  drill: readonly DrillChip[] | undefined,
  prev: AnswerContext | undefined,
  partialAccess?: PartialAccess,
): AnswerMessage {
  return {
    id: messageId,
    role: 'assistant',
    kind: 'answer',
    sampleId,
    content: drill ? { text, drill } : { text },
    footer: { tokens: 0 },
    context: prev ?? { path: [], filters: [], sources: [] },
    isSample: true,
    ...(partialAccess ? { partialAccess } : {}),
  };
}

function suggestionChips(granted: readonly ScopeKey[]): readonly DrillChip[] | undefined {
  const chips = SUGGESTED_PROMPTS.filter((p) => isAvailable(SAMPLE_ANSWERS[p.answerId], granted))
    .slice(0, 3)
    .map((p): DrillChip => ({
      id: `suggest-${p.id}`,
      label: p.text,
      action: { kind: 'prompt', text: p.text },
    }));
  return chips.length > 0 ? chips : undefined;
}

function moduleList(keys: readonly ScopeKey[]): string {
  return keys.map((key) => `«${SCOPE_LABELS[key]}»`).join('، ');
}

// ---------------------------------------------------------------------------
// Answer plan — what a request will be answered with, given the REAL scope
// ---------------------------------------------------------------------------

/**
 * Partial-access fallbacks (frame F): when the caller lacks a module a scripted
 * answer needs, the answer named here (which needs fewer modules) is used
 * instead — but only if the caller's scope fully covers THAT answer.
 */
export const PARTIAL_FALLBACKS: Readonly<Record<string, string>> = {
  'customer-profit': 'sales-by-customer',
  'daily-report': 'sales-sep',
};

/**
 * «تحلیل سنگین» (frame G). The design defines it as a period over a year, more
 * than three modules, or row-by-row costing. The period is only a label in the
 * demo, so the two content rules are modelled: the answers listed here compute
 * cost row by row, and any answer reading more than `MAX_LIGHT_MODULES` modules.
 */
export const HEAVY_ANSWERS: Readonly<Record<string, HeavyReason>> = {
  'customer-profit': 'costing',
};
export const MAX_LIGHT_MODULES = 3;
/** Estimate shown for a heavy analysis: never lower than the design's 4,500. */
export const HEAVY_ESTIMATE_TOKENS = ESTIMATE_MAX_TOKENS;

export type AccessKind =
  /** Every needed module is granted and selected. */
  | 'full'
  /** A needed module is not granted; a fallback answer covers what IS available (frame F). */
  | 'partial'
  /** Nothing can be answered: a needed module is not granted / not selected. */
  | 'refused'
  /** No scripted answer matches (generic notice or unknown entity). */
  | 'unscripted';

export interface AnswerPlan {
  readonly access: AccessKind;
  /** Scripted answer that will be rendered (the fallback for `partial`), or null. */
  readonly sampleId: string | null;
  /** Needed modules the caller is NOT granted. */
  readonly noAccess: readonly ScopeKey[];
  /** Needed modules the caller may read but unticked in the composer. */
  readonly deselected: readonly ScopeKey[];
  /** Modules the answer reads. */
  readonly modules: readonly ScopeKey[];
}

/** Decides, from the real grant and the composer selection, how a request is answered. */
export function planAnswer(request: EngineRequest, granted: readonly ScopeKey[]): AnswerPlan {
  const answerId =
    request.type === 'prompt' ? matchPrompt(request.text) : answerIdForAction(request.action);
  const sample = answerId ? SAMPLE_ANSWERS[answerId] : undefined;
  if (!sample) {
    return { access: 'unscripted', sampleId: null, noAccess: [], deselected: [], modules: [] };
  }

  // Typed prompts are limited to the modules ticked in the composer; drill-downs
  // only need the real grant (the chip existed because the previous answer did).
  const allowed = request.type === 'prompt' ? request.selected : granted;
  const missing = sample.requires.filter((key) => !allowed.includes(key));
  if (missing.length === 0) {
    return {
      access: 'full',
      sampleId: sample.id,
      noAccess: [],
      deselected: [],
      modules: sample.requires,
    };
  }
  const noAccess = missing.filter((key) => !granted.includes(key));
  const deselected = missing.filter((key) => granted.includes(key));

  const fallbackId = PARTIAL_FALLBACKS[sample.id];
  const fallback = fallbackId ? SAMPLE_ANSWERS[fallbackId] : undefined;
  if (
    noAccess.length > 0 &&
    deselected.length === 0 &&
    fallback &&
    fallback.requires.every((key) => allowed.includes(key))
  ) {
    return {
      access: 'partial',
      sampleId: fallback.id,
      noAccess,
      deselected,
      modules: fallback.requires,
    };
  }
  return { access: 'refused', sampleId: sample.id, noAccess, deselected, modules: [] };
}

/** Why the planned answer is a heavy analysis, or `null` when it is an ordinary question. */
export function heavyReason(plan: AnswerPlan): HeavyReason | null {
  if (plan.access !== 'full' || plan.sampleId === null) return null;
  const costing = HEAVY_ANSWERS[plan.sampleId];
  if (costing) return costing;
  return plan.modules.length > MAX_LIGHT_MODULES ? 'modules' : null;
}

// ---------------------------------------------------------------------------
// Ambiguity (frame E)
// ---------------------------------------------------------------------------

/**
 * AMBIGUITY RULE — a typed prompt is ambiguous (the assistant asks instead of
 * guessing, free of charge) when, after `normalizePrompt`:
 *
 *   1. «مشتری بزرگ» — it names a "big customer" (or «بزرگ‌ترین مشتری») without
 *      saying by which measure (the design's own example);
 *   2. «سودآوری» — it asks for profitability (سود / سودآوری) but names neither an
 *      entity (مشتری، کالا، محصول، پروژه، انبار) nor a period (ماه، فصل، سال،
 *      امروز، a month name …);
 *   3. one word — a single word that matches no scripted answer.
 *
 * Rules apply in that order, only to typed prompts (never to drill-downs or to
 * the option text of an earlier clarification), and a clarification is offered
 * only if at least two of its options are answerable with the caller's real
 * scope (`full` or `partial`) — otherwise the prompt goes on to the normal
 * access check.
 */
export interface ClarifySpec {
  readonly question: string;
  readonly options: readonly ClarifyOption[];
}

const SALES_BY_CUSTOMER_PROMPT = 'فروش این ماه به تفکیک مشتری';
const RECEIVABLES_PROMPT = 'مطالبات معوق بالای ۶۰ روز';
const PROFIT_PROMPT = 'سودآوری هر مشتری';

const BIG_CUSTOMER = /(?:^| )(?:مشتری(?:ان)? بزرگ|بزرگ ?ترین مشتری(?:ان)?)(?: |$)/;
const PROFIT_ENTITY_WORDS = ['مشتری', 'کالا', 'محصول', 'پروژه', 'انبار'] as const;
const PERIOD_WORDS = [
  'ماه',
  'فصل',
  'سال',
  'امروز',
  'دیروز',
  'هفته',
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

function isProfitWord(token: string): boolean {
  return token === 'سود' || token.startsWith('سودآور');
}

function answerableOptions(
  options: readonly ClarifyOption[],
  selected: readonly ScopeKey[],
  granted: readonly ScopeKey[],
): readonly ClarifyOption[] {
  return options.filter((option) => {
    const access = planAnswer({ type: 'prompt', text: option.prompt, selected }, granted).access;
    return access === 'full' || access === 'partial';
  });
}

/** Returns what to ask, or `null` when the prompt is clear enough. See the rule above. */
export function detectAmbiguity(
  text: string,
  selected: readonly ScopeKey[],
  granted: readonly ScopeKey[],
): ClarifySpec | null {
  const normalized = normalizePrompt(text);
  if (normalized === '') return null;
  const tokens = normalized.split(' ');

  let spec: ClarifySpec | null = null;
  if (BIG_CUSTOMER.test(normalized)) {
    spec = {
      question: 'منظورتان از «مشتری بزرگ» کدام است؟',
      options: [
        { id: 'amount', label: 'بیشترین مبلغ خرید', prompt: SALES_BY_CUSTOMER_PROMPT },
        { id: 'orders', label: 'بیشترین تعداد سفارش', prompt: SALES_BY_CUSTOMER_PROMPT },
        { id: 'receivables', label: 'بیشترین مطالبات باز', prompt: RECEIVABLES_PROMPT },
      ],
    };
  } else if (
    tokens.some(isProfitWord) &&
    !tokens.some((token) => PROFIT_ENTITY_WORDS.some((word) => token.startsWith(word))) &&
    !tokens.some((token) => PERIOD_WORDS.includes(token as (typeof PERIOD_WORDS)[number]))
  ) {
    spec = {
      question: 'سودآوری را برای کدام مورد می‌خواهید؟',
      options: [
        { id: 'customer', label: 'سودآوری هر مشتری', prompt: PROFIT_PROMPT },
        {
          id: 'sales-only',
          label: 'فروش به تفکیک مشتری (بدون سود)',
          prompt: SALES_BY_CUSTOMER_PROMPT,
        },
      ],
    };
  } else if (tokens.length === 1 && matchPrompt(text) === null) {
    spec = {
      question: 'پرسش شما کوتاه است؛ کدام یک از این‌ها را می‌خواهید؟',
      options: SUGGESTED_PROMPTS.map((prompt) => ({
        id: prompt.id,
        label: prompt.text,
        prompt: prompt.text,
      })),
    };
  }
  if (!spec) return null;

  const options = answerableOptions(spec.options, selected, granted).slice(0, 3);
  return options.length >= 2 ? { question: spec.question, options } : null;
}

/**
 * The single place an answer is produced. `messageId` is allocated by the
 * caller so the answer's path steps can point back at its own message.
 */
export function answerFor(
  request: EngineRequest,
  messageId: string,
  { granted, prev }: EngineContext,
): AnswerMessage {
  const plan = planAnswer(request, granted);
  const sample = plan.sampleId ? SAMPLE_ANSWERS[plan.sampleId] : undefined;

  if (!sample) {
    if (request.type === 'drill' && request.action.kind === 'entity') {
      const hasFeatured = isAvailable(SAMPLE_ANSWERS['customer-detail'], granted);
      return noticeAnswer(
        'entity-missing',
        messageId,
        [
          `برای «${request.action.label}» در نسخهٔ نمایشی پاسخ نمونه‌ای تعریف نشده است.`,
          ...(hasFeatured ? [' ریزشدن لایه‌به‌لایه را می‌توانید روی «فولاد آریا» ببینید.'] : []),
        ],
        undefined,
        prev,
      );
    }
    return noticeAnswer(
      'generic',
      messageId,
      [
        'این پاسخ نمونه است و بر پایهٔ دادهٔ واقعی ساخته نشده. در نسخهٔ نمایشی فقط چند پرسش آماده پاسخ داده می‌شود؛ یکی از پرسش‌های زیر را امتحان کنید.',
      ],
      suggestionChips(granted),
      prev,
    );
  }

  if (plan.access === 'refused') {
    const needed = SAMPLE_ANSWERS[plan.sampleId ?? '']?.requires ?? [];
    return noticeAnswer(
      'needs-scope',
      messageId,
      [
        `برای پاسخ به این پرسش داده‌های ${moduleList(needed)} لازم است.`,
        ...(plan.deselected.length > 0
          ? [` ${moduleList(plan.deselected)} را در محدودهٔ پرسش فعال کنید و دوباره بپرسید.`]
          : []),
      ],
      suggestionChips(granted),
      prev,
      // The soft notice above the text (frame F) names the modules the caller cannot read.
      plan.noAccess.length > 0 ? { missing: plan.noAccess, available: [] } : undefined,
    );
  }

  // `partial` answers the fallback sample and says plainly what was left out.
  const partial = plan.access === 'partial';
  const content = adaptContent(sample.content, granted);
  return {
    id: messageId,
    role: 'assistant',
    kind: 'answer',
    sampleId: sample.id,
    content: partial
      ? {
          ...content,
          text: [
            ...content.text,
            ` بخش‌هایی که به داده‌های ${moduleList(plan.noAccess)} نیاز دارند در این پاسخ محاسبه نشده‌اند.`,
          ],
        }
      : content,
    footer: sample.footer,
    context: {
      path: buildPath(sample.path, messageId, prev),
      filters: sample.filters,
      sources: sample.sources,
    },
    isSample: true,
    ...(partial ? { partialAccess: { missing: plan.noAccess, available: plan.modules } } : {}),
  };
}

/** The user-visible text of the bubble a drill action shows. */
export function drillPromptText(action: DrillAction): string {
  switch (action.kind) {
    case 'chip':
      return action.label;
    case 'entity':
      return `جزئیات «${action.label}»`;
    case 'prompt':
      return action.text;
  }
}

/** Seed history of a bundled sample conversation (prompt + its scripted answer). */
export function seedConversation(
  conversation: SampleConversation,
  granted: readonly ScopeKey[],
): readonly ChatMessage[] {
  const answerMessage = answerFor(
    { type: 'drill', action: { kind: 'prompt', text: conversation.prompt } },
    `${conversation.id}:answer`,
    { granted },
  );
  return [
    {
      id: `${conversation.id}:prompt`,
      role: 'user',
      kind: 'prompt',
      text: conversation.prompt,
      scope: granted,
      period: 'this-month',
    },
    answerMessage,
  ];
}
